import { INCIDENT_STATUSES, PLAN_SIZE_TIERS, SEVERITIES } from '@prerender/diagnostics-api';

import { optionsWithAddressExtras } from './filterOptions';
import type { IncidentListParams } from './incidentListParams';
import { incidentStatusLabel } from './incidentStatusLabels';
import { INCIDENT_TYPE_LABELS, incidentTypeLabel } from './incidentTypeLabels';
import MultiSelectFilter, { type MultiSelectOption } from './MultiSelectFilter';
import { planSizeTierLabel } from './planSizeTierLabels';
import { severityLabel } from './severityLabels';
import type { IncidentListFilterPatch } from './useIncidentListParams';

export type IncidentColumnFilterKey = 'incidentType' | 'severity' | 'status' | 'planTier';

type IncidentColumnFilterSpec = {
  label: string;
  options: (params: IncidentListParams) => readonly MultiSelectOption[];
  selected: (params: IncidentListParams) => readonly string[];
  patch: (values: string[]) => IncidentListFilterPatch;
};

const optionsOf = <T extends string>(values: readonly T[], labelOf: (value: T) => string): MultiSelectOption[] =>
  values.map((value) => ({ value, label: labelOf(value) }));

const LABELLED_INCIDENT_TYPES = Object.keys(INCIDENT_TYPE_LABELS);

const incidentTypeOptions = (params: IncidentListParams): MultiSelectOption[] =>
  optionsWithAddressExtras(LABELLED_INCIDENT_TYPES, params.incidentTypes, incidentTypeLabel);

export const INCIDENT_COLUMN_FILTERS: Record<IncidentColumnFilterKey, IncidentColumnFilterSpec> = {
  incidentType: {
    label: 'type',
    options: incidentTypeOptions,
    selected: (params) => params.incidentTypes,
    patch: (values) => ({ incidentTypes: values }),
  },
  severity: {
    label: 'severity',
    options: () => optionsOf(SEVERITIES, severityLabel),
    selected: (params) => params.severities,
    patch: (values) => ({ severities: values as IncidentListParams['severities'] }),
  },
  status: {
    label: 'status',
    options: () => optionsOf(INCIDENT_STATUSES, incidentStatusLabel),
    selected: (params) => params.statuses,
    patch: (values) => ({ statuses: values as IncidentListParams['statuses'] }),
  },
  planTier: {
    label: 'plan',
    options: () => optionsOf(PLAN_SIZE_TIERS, planSizeTierLabel),
    selected: (params) => params.planTiers,
    patch: (values) => ({ planTiers: values as IncidentListParams['planTiers'] }),
  },
};

type IncidentColumnFilterProps = {
  filterKey: IncidentColumnFilterKey;
  params: IncidentListParams;
  onChange: (patch: IncidentListFilterPatch) => void;
};

function IncidentColumnFilter({ filterKey, params, onChange }: IncidentColumnFilterProps) {
  const spec = INCIDENT_COLUMN_FILTERS[filterKey];

  return (
    <MultiSelectFilter
      name={filterKey}
      label={spec.label}
      options={spec.options(params)}
      selected={spec.selected(params)}
      onChange={(values) => onChange(spec.patch(values))}
    />
  );
}

export default IncidentColumnFilter;
